import { useCallback, useEffect, useId, useRef, useState } from "react";
import { usePointerDrag } from "../hooks/usePointerDrag";
import type { DragSource } from "../hooks/usePointerDrag";
import { toApiError } from "../../../lib/http/errors";
import { Button, ButtonLink } from "../../../components/ui/Button";
import type { TopBarSpec } from "../../../components/TopBar";
import { useModalFocus } from "../../../hooks/useModalFocus";
import { useT } from "../../../i18n";
import { SegGroup } from "../../../components/ui/Seg";
import { videoPath } from "../../../constants/paths";
import type {
  ConsoleRoom,
  RelayOp,
  CheckChain,
  RelayDraft,
  RelayInfo,
  RelayIssue,
  RelayStageRun,
  RelayStudent,
  Trigger,
} from "../types";
import {
  alignIn,
  draftOf,
  mismatchStages,
  moveStage,
  place,
  randomize,
  remove,
  rulesOf,
  sameDraft,
  setKind,
  setTimeoutS,
  toStagePlans,
  TRIGGER_ICON,
  unplaced,
  ROOM_REPLAY_PLAYER,
} from "../lib/relay";
import { PlanRejected, RelayRejected } from "../api/relayApi";
import { useCourseApi } from "../../../lib/api/courseApi";
import { relayWarns } from "../lib/relayMotion";
import { useRelayMotions } from "../hooks/useRelayMotion";
import type { RelayApi } from "../api/relayApi";
import "../../../styles/relay.css";

/** 학생의 현재 스냅샷(서버가 주면) */
const byPidSid = (students: readonly RelayStudent[], pid: string) =>
  students.find((x) => x.pid === pid)?.snapshotId ?? null;

/** 배치·진행은 1초마다 다시 읽는다(콘솔과 같다) */
export const RELAY_POLL_MS = 1000;

/** 1초마다 읽기 — 겹쳐 부르지 않는다 */
export function useRelayPoll(api: RelayApi | null, everyMs = RELAY_POLL_MS) {
  const [data, setData] = useState<RelayInfo | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inflight = useRef(false);
  const refresh = useCallback(async () => {
    if (!api || inflight.current) return;
    inflight.current = true;
    try {
      setData(await api.get());
      setError(null);
    } catch (e) {
      setError(toApiError(e).code);
    } finally {
      inflight.current = false;
    }
  }, [api]);
  useEffect(() => {
    if (!api) return;
    void refresh();
    if (everyMs <= 0) return;
    const h = window.setInterval(() => void refresh(), everyMs);
    return () => window.clearInterval(h);
  }, [api, refresh, everyMs]);
  return { data, error, refresh };
}

/** 정합 검사 함수(@picnic-field/modes — 룸 서버 · 제어 평면과 같은 규칙)를 늦게 불러온다(ajv 등이 첫 화면 묶음에 끼지 않게) */
export function useCheckChain(): CheckChain | null {
  const [check, setCheck] = useState<CheckChain | null>(null);
  useEffect(() => {
    let alive = true;
    void import("@picnic-field/modes").then((m) => {
      if (alive) setCheck(() => m.checkRelayChain);
    });
    return () => {
      alive = false;
    };
  }, []);
  return check;
}

/** 끌고 있는 곳 — 구간 번호(그 자리에 넣기) · "end"(맨 뒤에 넣기) · "pool"(배치에서 빼기) */
type Target = number | "end" | "pool";
type DragBind = DragSource;

/**
 * FS-08-01 릴레이 순서 배치(교사 · SCR v0.6 sfs-08-01 + v0.7 검토 변경 v07-03~08) — 콘솔 탭 /teacher/room/:roomId/relay.
 *  실행 전(배치): 🚩 출발 장치 → 구간 카드 1~n(별명 · 들어오는 것 → 내보내는 것) → ＋ 빈 칸 → 🏁 결승 장치
 *   · 끌어 놓기 — 미배치 학생을 카드 자리/빈 칸에, 카드끼리 순서 바꾸기, 미배치 칸으로 빼기
 *   · 키보드 — 학생을 눌러 고르고(1) 놓을 카드/빈 칸을 누른다(2) · Esc 취소 · 고른 카드는 ◀ ▶ 로 옮기고 ↩ 로 뺀다
 *   · 구간 편집 — 들어오는 것(공 도달 · 레버 눌림 · 존 진입) · 내보내는 것(공 굴리기 · 레버 누르기 · 존으로 밀기) · 타임아웃(선택지)
 *   · 정합 검사(checkRelayChain) — 틀린 구간 번호 배너 + 카드 빨간 연결선 · '맞추기' 한 번에 고치기 · 틀리면 ▶ 시작 막힘
 *   · 미배치 · 끊김 표시 · 🎲 무작위(늘 정합) · 👁 배치도 미리보기(모의 실행 없음) · 💾 저장(PUT plan — "저장됨" 표시)
 *  시작 뒤(진행 — v07-04): 순서·트리거 잠금 · 진행 막대 · 구간 표(상태 · 시도 — 별명·시도 횟수는 교사 화면에만)
 *   · ⏸ 일시정지 / ▶ 재개 · 지금 구간 ⟲ 다시 · ⏭ 건너뛰기
 * 실패 학생 별명은 교사 화면에만 둔다(DR-01 은 학생·칠판 화면 규칙 — 학생 쪽은 '구간 n · 다시 준비하는 중'만).
 */
export function RelayPanel({
  room,
  api,
  courseLabel,
  onTop,
  onError,
  onDone,
}: {
  room: ConsoleRoom;
  api: RelayApi;
  courseLabel: string;
  onTop: (spec: TopBarSpec | null) => void;
  onError: (e: unknown) => void;
  onDone: (text: string) => void;
}) {
  const t = useT();
  const { data, error, refresh } = useRelayPoll(api);
  const check = useCheckChain();
  const [draft, setDraft] = useState<RelayDraft | null>(null);
  const [base, setBase] = useState<RelayDraft | null>(null);
  const [selected, setSelected] = useState<string | null>(null);
  const [picked, setPicked] = useState<string | null>(null);
  // 끌어 놓기 — 놓을 때 putAt(아래에 정의 · 부를 때는 이미 있다)
  const dnd = usePointerDrag<Target>((pid, target) => putAt(pid, target));
  const [live, setLive] = useState("");
  const [busy, setBusy] = useState<string | null>(null);
  const [serverIssues, setServerIssues] = useState<RelayIssue[] | null>(null);
  /** 룸 서버가 받지 않은 명령·배치(422 error · 409) — 이유 문구 */
  const [problem, setProblem] = useState<string | null>(null);
  const [preview, setPreview] = useState(false);
  /** 완성 뒤 '새 판'을 눌렀다 — 서버가 아직 allDone 이어도(배치 저장이 거절됐을 때) 배치 화면을 연다 · 시작하면 풀린다 */
  const [fresh, setFresh] = useState(false);

  // 서버 배치를 받으면 — 초안이 없거나 손대지 않았으면 그것으로(다른 기기에서 저장한 배치도 따라온다)
  const planKey = data ? JSON.stringify(data.plan) : null;
  useEffect(() => {
    if (!data) return;
    const server = draftOf(data.plan);
    setBase(server);
    setDraft((d) => (!d || (base && sameDraft(d, base)) ? server : d));
    // 서버 배치가 바뀔 때만
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planKey]);

  const setupView = !!data && (data.state === "setup" || (fresh && data.state === "allDone"));
  const running = data ? !setupView : false;
  useEffect(() => {
    if (data?.state === "running") setFresh(false);
  }, [data?.state]);
  const def = data?.def;
  const students = data?.students ?? [];
  // 2026-09-30 릴레이 준비 경고(SR-MODE-07 부기) — 리모컨 없이 움직이지 않는 프로그램은 카드에 ⚠(시작 전 · 서버가 snapshotId 를 줄 때)
  const course = useCourseApi();
  const motions = useRelayMotions(
    course ? (sid) => course.runBundle(sid) : null,
    students.map((s) => s.snapshotId),
    data?.state === "setup",
  );
  const warnOf = (pid: string) => {
    const sid = byPidSid(students, pid);
    return !running && !!sid && relayWarns(motions.get(sid));
  };
  const byPid = new Map(students.map((s) => [s.pid, s]));
  const planNames = new Map((data?.plan ?? []).map((p) => [p.pid, p.nickname]));
  const nameOf = (pid: string) => byPid.get(pid)?.nickname ?? planNames.get(pid) ?? pid;
  const stages = draft?.stages ?? [];
  const dirty = !!draft && !!base && !sameDraft(draft, base);
  const issues: RelayIssue[] | null =
    draft && def && check ? check(toStagePlans(draft, students), rulesOf(def)) : null;
  const bad = mismatchStages(issues ?? []);
  const canStart = !!issues && issues.length === 0 && !running && busy === null;

  // 상단 바 — 🔗 3학년 2반 · 릴레이 기계 · 배치 n/30 · 실행 전 | 진행 중 · 구간 n | 일시정지 | 완성
  const phaseLabel = !data
    ? ""
    : setupView
      ? t("relay.phase.setup")
      : data.state === "allDone"
        ? t("relay.phase.allDone")
        : data.state === "running"
          ? data.paused
            ? t("relay.phase.paused")
            : t("relay.phase.running", { n: (data.current ?? 0) + 1 })
          : t("relay.phase.setup");
  const topKey = JSON.stringify([courseLabel, stages.length, def?.maxStages, phaseLabel, dirty]);
  useEffect(() => {
    onTop({
      icon: "🔗",
      title: t("relay.topTitle", { course: courseLabel }),
      chips: data
        ? [
            {
              label: t("relay.chipPlaced", { n: stages.length, max: def?.maxStages ?? 30 }),
              testId: "relay-count",
            },
            { label: phaseLabel, testId: "relay-phase" },
            ...(!running
              ? [
                  {
                    label: t(dirty ? "relay.draft" : "relay.saved"),
                    testId: "relay-saved",
                  },
                ]
              : []),
          ]
        : [],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topKey, onTop, t]);
  useEffect(() => () => onTop(null), [onTop]);

  // 선택 카드가 없거나 사라지면 — 첫 불일치 구간(와이어: 구간 7 선택) · 없으면 첫 구간
  const firstBad = [...bad].sort((a, b) => a - b)[0];
  const selIndex = stages.findIndex((s) => s.pid === selected);
  useEffect(() => {
    if (running || !draft) return;
    if (selIndex >= 0) return;
    const pick = stages[firstBad ?? 0];
    if (pick) setSelected(pick.pid);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, draft === null, selIndex, firstBad]);

  if (error && !data)
    return (
      <section className="pf-pane pf-pane--hero" data-testid="relay-load-failed">
        <p className="pf-muted">
          {t("relay.loadFailed")} · {error}
        </p>
      </section>
    );
  if (!data || !draft || !def) return null;
  if (!data.enabled)
    return (
      <section className="pf-pane pf-pane--hero" data-testid="relay-off">
        <h2 className="pf-h3">{t("relay.offTitle")}</h2>
        <p className="pf-muted">{t("relay.offText")}</p>
      </section>
    );

  const edit = (next: RelayDraft) => {
    if (next === draft) return;
    setDraft(next);
    setServerIssues(null);
  };
  const stageName = (i: number) => t("relay.stage", { n: i + 1 });
  const putAt = (pid: string, to: Target) => {
    if (to === "pool") {
      edit(remove(draft, pid));
      setLive(t("relay.movedOut", { name: nameOf(pid) }));
    } else {
      const at = to === "end" ? stages.length : to;
      const next = place(draft, pid, at, def.maxStages, def.triggers[0]);
      if (next === draft) {
        if (stages.length >= def.maxStages) setLive(t("relay.full", { max: def.maxStages }));
        return;
      }
      edit(next);
      const idx = next.stages.findIndex((s) => s.pid === pid);
      setSelected(pid);
      setLive(t("relay.moved", { name: nameOf(pid), stage: stageName(idx) }));
    }
    setPicked(null);
  };
  const pick = (pid: string) => {
    if (picked === pid) {
      setPicked(null);
      return;
    }
    setPicked(pid);
    setLive(t("relay.picked", { name: nameOf(pid) }));
  };

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setProblem(null);
    try {
      await fn();
      await refresh();
    } catch (e) {
      if (e instanceof PlanRejected) setServerIssues(e.issues);
      else if (e instanceof RelayRejected)
        setProblem(
          ["locked", "invalid-plan", "not-running", "not-current", "state", "paused"].includes(
            e.reason,
          )
            ? t(`relay.reject.${e.reason}`)
            : t("relay.reject.state"),
        );
      else onError(e);
    } finally {
      setBusy(null);
    }
  };
  const save = () =>
    run("save", async () => {
      await api.savePlan(draft);
      setBase(draft);
      onDone(t("relay.savedToast"));
    });
  const start = () =>
    run("start", async () => {
      // 새 판인데 서버가 아직 완성 판이면(저장이 거절됐었다) 배치를 먼저 보낸다 — 룸 서버가 Setup 으로 다시 싣는다
      if (dirty || data.state !== "setup") {
        await api.savePlan(draft);
        setBase(draft);
      }
      await api.cmd("start");
      onDone(t("relay.startedToast"));
    });
  const cmd = (op: RelayOp, stage?: number) =>
    run(`${op}:${stage ?? ""}`, () => api.cmd(op, stage));
  // 새 판(2026-09-30 · SR-MODE-07) — 완성 뒤 같은 순서·설정을 다시 저장(PUT plan) → 룸 서버 Setup · 학생 '준비 중' · 다 됐어 초기화
  //  · 제어 평면은 새 판(run_no + 1) · 앞 판은 완성 · 리플레이 그대로. 거절되면(학생 기계 없음 등) 배치 화면에서 고친다
  const newRound = () =>
    run("newRound", async () => {
      const same = draftOf(data.plan);
      setFresh(true);
      setDraft(same);
      setBase(same);
      await api.savePlan(same);
      onDone(t("relay.newRoundToast"));
    });

  if (running)
    return (
      <RelayProgress
        info={data}
        busy={busy}
        problem={problem}
        roomPaused={room.state === "paused"}
        roomId={room.roomId}
        onCmd={(op, stage) => void cmd(op, stage)}
        onNewRound={() => void newRound()}
      />
    );

  // ── 끌어 놓기(포인터 — 마우스 · 터치 · 펜 · Firefox 모두 · usePointerDrag) ──
  const over = dnd.over;
  const dragProps = (pid: string): DragBind => dnd.source(pid, nameOf(pid));
  const dropProps = (target: Target) => dnd.target(target);

  const pool = unplaced(draft, students);
  const sel = selIndex >= 0 ? stages[selIndex]! : null;
  const connOf = (pid: string) => byPid.get(pid)?.connection ?? "offline";
  const shown = serverIssues ?? issues ?? [];
  const mismatches = shown.filter((x) => x.code === "mismatch" && x.stage !== null);
  const others = shown.filter((x) => x.code !== "mismatch");
  const pickedName = picked ? nameOf(picked) : null;
  // P3-S2 — 학생이 내 구간 화면에서 누른 '✓ 다 됐어'(Setup 확정 표시 · 저장된 배치 기준 — 진행에는 영향 없음)
  const readyPids = new Set(
    (data?.plan ?? [])
      .filter((p) => data?.stages.find((x) => x.stage === p.stage)?.ready)
      .map((p) => p.pid),
  );

  return (
    <div
      className="pf-relay"
      data-testid="fs-08-01-panel"
      data-state="setup"
      onKeyDown={(e) => {
        if (e.key === "Escape") setPicked(null);
      }}
    >
      <section className="pf-pane pf-pane--hero pf-relay__plan" aria-labelledby="rl-title">
        <div className="pf-relay__head">
          <h2 id="rl-title" className="pf-h3">
            {t("relay.planTitle")}
          </h2>
          <span className="pf-muted">{t("relay.planHint")}</span>
          {stages.length > 0 && (
            <span className="pf-chip-static pf-chip-static--ok" data-testid="relay-ready-count">
              {t("relay.readyCount", {
                n: stages.filter((x) => readyPids.has(x.pid)).length,
                total: stages.length,
              })}
            </span>
          )}
          <div className="pf-row pf-rc__end">
            <Button
              variant="ghost"
              className="pf-btn--sm"
              disabled={busy !== null}
              data-testid="relay-random"
              onClick={() =>
                edit(randomize(draft, students, { max: def.maxStages, triggers: def.triggers }))
              }
            >
              {t("relay.random")}
            </Button>
            <Button
              variant="ghost"
              className="pf-btn--sm"
              data-testid="relay-preview"
              onClick={() => setPreview(true)}
            >
              {t("relay.preview")}
            </Button>
            <Button
              variant="ghost"
              className="pf-btn--sm"
              disabled={!dirty || busy !== null || !issues || issues.length > 0}
              title={issues && issues.length > 0 ? t("relay.startBlocked") : undefined}
              data-testid="relay-save"
              onClick={() => void save()}
            >
              {t("relay.save")}
            </Button>
            <Button
              variant="accent"
              className="pf-btn--sm"
              disabled={!canStart}
              data-testid="relay-start"
              title={canStart ? undefined : t("relay.startBlocked")}
              onClick={() => void start()}
            >
              {t("relay.start")}
            </Button>
          </div>
        </div>
        {room.modeId !== "relay-machine" && (
          <p className="pf-muted" data-testid="relay-mode-note">
            {t("relay.modeNote")}
          </p>
        )}
        {(mismatches.length > 0 || others.length > 0) && (
          <div className="pf-banner pf-banner--warn" role="alert" data-testid="relay-mismatch">
            <div className="pf-banner__text">
              <b>{t("relay.cannotStart")}</b>{" "}
              {mismatches.length > 0 && (
                <span>
                  {t("relay.mismatchAt")}{" "}
                  {mismatches.map((x) => (
                    <button
                      key={x.stage}
                      type="button"
                      className="pf-relay__jump"
                      data-jump={x.stage! + 1}
                      onClick={() => {
                        const s = stages[x.stage!];
                        if (s) setSelected(s.pid);
                      }}
                    >
                      {stageName(x.stage!)}
                    </button>
                  ))}
                </span>
              )}
              {others.map((x, i) => (
                <span key={`${x.code}-${i}`} data-issue={x.code}>
                  {" "}
                  {issueText(t, x, def, stages.length)}
                </span>
              ))}
            </div>
          </div>
        )}
        {problem && (
          <p className="pf-field-err" role="alert" data-testid="relay-problem">
            {problem}
          </p>
        )}
        <ol
          className="pf-relay__slots"
          data-testid="relay-slots"
          aria-label={t("relay.slotsLabel")}
        >
          <li className="pf-relay__card pf-relay__card--fixed" data-testid="relay-start-device">
            <span className="pf-relay__no">{t("relay.startDevice")}</span>
            <span className="pf-muted">{t("relay.startDeviceNote")}</span>
          </li>
          {stages.map((s, i) => {
            const conn = connOf(s.pid);
            const isSel = selected === s.pid && !picked;
            const cls = [
              "pf-relay__card",
              isSel && "pf-relay__card--sel",
              bad.has(i) && "pf-relay__card--bad",
              over === i && "pf-relay__card--target",
              picked === s.pid && "pf-relay__card--picked",
            ]
              .filter(Boolean)
              .join(" ");
            return (
              <li key={s.pid} className={cls} data-stage={i + 1} data-pid={s.pid} {...dropProps(i)}>
                <button
                  type="button"
                  className="pf-relay__cardbtn"
                  aria-pressed={picked ? undefined : isSel}
                  aria-label={
                    picked && picked !== s.pid
                      ? t("relay.putHere", { name: pickedName ?? "", stage: stageName(i) })
                      : t("relay.cardLabel", {
                          stage: stageName(i),
                          name: nameOf(s.pid),
                          in: t(`relay.kind.${s.in}`),
                          out: t(`relay.kind.${s.out}`),
                        }) + (warnOf(s.pid) ? ` · ${t("relay.cardWarn")}` : "")
                  }
                  data-testid="relay-card"
                  onClick={() => {
                    if (picked && picked !== s.pid) putAt(picked, i);
                    else if (picked === s.pid) setPicked(null);
                    else setSelected(s.pid);
                  }}
                  {...dragProps(s.pid)}
                >
                  <span className="pf-relay__no">{stageName(i)}</span>
                  <b className="pf-relay__name">
                    {nameOf(s.pid)}
                    {conn !== "online" && (
                      <span
                        className="pf-chip-static pf-chip-static--err pf-relay__conn"
                        data-conn={conn}
                      >
                        {t(`relay.conn.${conn}`)}
                      </span>
                    )}
                  </b>
                  <span className="pf-relay__kinds">
                    <KindTag k={s.in} /> → <KindTag k={s.out} />
                  </span>
                  {warnOf(s.pid) && (
                    <span
                      className="pf-chip-static pf-chip-static--warn pf-relay__warn"
                      data-testid="relay-card-warn"
                      title={t("relay.cardWarn")}
                    >
                      ⚠
                    </span>
                  )}
                  {readyPids.has(s.pid) && (
                    <span
                      className="pf-chip-static pf-chip-static--ok pf-relay__ready"
                      data-testid="relay-card-ready"
                    >
                      {t("relay.readyChip")}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
          {stages.length < def.maxStages && (
            <li
              className={`pf-relay__card pf-relay__card--empty${over === "end" ? " pf-relay__card--target" : ""}`}
              {...dropProps("end")}
            >
              <button
                type="button"
                className="pf-relay__cardbtn"
                disabled={!picked}
                aria-label={t("relay.addEnd", { stage: stageName(stages.length) })}
                data-testid="relay-add"
                onClick={() => picked && putAt(picked, "end")}
              >
                <span className="pf-relay__no">{stageName(stages.length)}</span>
                <span className="pf-muted">{t("relay.emptySlot")}</span>
              </button>
            </li>
          )}
          <li className="pf-relay__card pf-relay__card--fixed" data-testid="relay-finish-device">
            <span className="pf-relay__no">{t("relay.finishDevice")}</span>
            <span className="pf-muted">{t("relay.finishDeviceNote")}</span>
          </li>
        </ol>
        <p className="pf-muted">{t("relay.keyboardHint")}</p>
        <p className="pf-sr-only" aria-live="polite">
          {live}
        </p>
      </section>

      <div className="pf-relay__body">
        <section
          className={`pf-pane pf-relay__pool${over === "pool" ? " pf-relay__pool--target" : ""}`}
          aria-labelledby="rl-pool"
          data-testid="relay-pool"
          {...dropProps("pool")}
        >
          <h2 id="rl-pool" className="pf-h3">
            {t("relay.unplaced", { n: pool.length })}
          </h2>
          {picked && stages.some((s) => s.pid === picked) && (
            <Button
              variant="ghost"
              className="pf-btn--sm pf-tchip--add"
              data-testid="relay-to-pool"
              onClick={() => putAt(picked, "pool")}
            >
              {t("relay.toPool")}
            </Button>
          )}
          <div className="pf-relay__list">
            {pool.map((s) => (
              <PoolChip
                key={s.pid}
                s={s}
                picked={picked === s.pid}
                onPick={() => pick(s.pid)}
                drag={dragProps(s.pid)}
              />
            ))}
            {pool.length === 0 && <p className="pf-muted">{t("relay.allPlaced")}</p>}
          </div>
          <p className="pf-relay__note pf-muted">{t("relay.poolNote")}</p>
        </section>

        <section
          className="pf-pane pf-relay__edit"
          aria-labelledby="rl-edit"
          data-testid="relay-edit"
        >
          {sel ? (
            <StageEditor
              index={selIndex}
              name={nameOf(sel.pid)}
              stage={sel}
              prev={selIndex > 0 ? { index: selIndex - 1, ...stages[selIndex - 1]! } : null}
              prevName={selIndex > 0 ? nameOf(stages[selIndex - 1]!.pid) : ""}
              mismatch={bad.has(selIndex)}
              count={stages.length}
              def={def}
              onKind={(side, k) => edit(setKind(draft, selIndex, side, k))}
              onTimeout={(sec) => edit(setTimeoutS(draft, selIndex, sec))}
              onAlign={() => edit(alignIn(draft, selIndex))}
              onMove={(d) => {
                const next = moveStage(draft, selIndex, selIndex + d);
                edit(next);
                setLive(
                  t("relay.moved", { name: nameOf(sel.pid), stage: stageName(selIndex + d) }),
                );
              }}
              onRemove={() => putAt(sel.pid, "pool")}
            />
          ) : (
            <>
              <h2 id="rl-edit" className="pf-h3">
                {t("relay.editTitleEmpty")}
              </h2>
              <p className="pf-muted">{t("relay.editEmpty")}</p>
            </>
          )}
          <div className="pf-banner pf-banner--ok" role="note">
            <div className="pf-banner__text">{t("relay.studentNote")}</div>
          </div>
        </section>
      </div>

      {preview && (
        <PreviewModal
          stages={stages.map((s) => ({ ...s, name: nameOf(s.pid) }))}
          bad={bad}
          onClose={() => setPreview(false)}
        />
      )}
      {dnd.ghost}
    </div>
  );
}

type T = ReturnType<typeof useT>;

function issueText(t: T, x: RelayIssue, def: RelayInfo["def"], n: number): string {
  const stage = x.stage !== null ? x.stage + 1 : 0;
  switch (x.code) {
    case "count":
      return t("relay.issue.count", { min: def.minStages, max: def.maxStages, n });
    case "duplicate":
      return t("relay.issue.duplicate", { n: stage });
    case "trigger":
      return t("relay.issue.trigger", { n: stage });
    case "timeout":
      return t("relay.issue.timeout", { n: stage });
    default:
      return t("relay.issue.mismatch", { n: stage });
  }
}

function KindTag({ k }: { k: Trigger }) {
  const t = useT();
  return (
    <span className="pf-relay__kind" data-kind={k}>
      <span aria-hidden="true">{TRIGGER_ICON[k]}</span> {t(`relay.kind.${k}`)}
    </span>
  );
}

function PoolChip({
  s,
  picked,
  onPick,
  drag,
}: {
  s: RelayStudent;
  picked: boolean;
  onPick: () => void;
  drag: DragBind;
}) {
  const t = useT();
  const off = s.connection !== "online";
  return (
    <button
      type="button"
      className="pf-btn pf-btn--ghost pf-btn--sm pf-tchip"
      aria-pressed={picked}
      aria-label={t("relay.pick", { name: s.nickname })}
      data-pid={s.pid}
      data-conn={s.connection}
      data-testid="relay-chip"
      onClick={onPick}
      {...drag}
    >
      {s.connection === "dropped" && <span aria-hidden="true">🔴</span>}
      {s.nickname}
      {off && ` · ${t(`relay.conn.${s.connection}`)}`}
    </button>
  );
}

function StageEditor({
  index,
  name,
  stage,
  prev,
  prevName,
  mismatch,
  count,
  def,
  onKind,
  onTimeout,
  onAlign,
  onMove,
  onRemove,
}: {
  index: number;
  name: string;
  stage: RelayDraft["stages"][number];
  prev: (RelayDraft["stages"][number] & { index: number }) | null;
  prevName: string;
  mismatch: boolean;
  count: number;
  def: RelayInfo["def"];
  onKind: (side: "in" | "out", k: Trigger) => void;
  onTimeout: (sec: number | null) => void;
  onAlign: () => void;
  onMove: (d: -1 | 1) => void;
  onRemove: () => void;
}) {
  const t = useT();
  const id = useId();
  const choices = def.timeoutChoicesS;
  const timeout = stage.timeoutS ?? def.stageTimeoutS;
  return (
    <>
      <div className="pf-row">
        <h2 id="rl-edit" className="pf-h3">
          {t("relay.editTitle", { stage: t("relay.stage", { n: index + 1 }), name })}
        </h2>
        <div className="pf-row pf-rc__end">
          <Button
            variant="ghost"
            className="pf-btn--sm"
            disabled={index === 0}
            aria-label={t("relay.earlierOf", { name })}
            data-testid="relay-earlier"
            onClick={() => onMove(-1)}
          >
            {t("relay.earlier")}
          </Button>
          <Button
            variant="ghost"
            className="pf-btn--sm"
            disabled={index >= count - 1}
            aria-label={t("relay.laterOf", { name })}
            data-testid="relay-later"
            onClick={() => onMove(1)}
          >
            {t("relay.later")}
          </Button>
          <Button
            variant="ghost"
            className="pf-btn--sm"
            aria-label={t("relay.removeOf", { name })}
            data-testid="relay-remove"
            onClick={onRemove}
          >
            {t("relay.remove")}
          </Button>
        </div>
      </div>
      {(["in", "out"] as const).map((side) => (
        <SegGroup
          key={side}
          label={t(side === "in" ? "relay.in" : "relay.out")}
          variant="green"
          testId={`relay-${side}`}
          invalid={side === "in" && mismatch}
        >
          {def.triggers.map((k) => (
            <label key={k} className="pf-seg__opt">
              <input
                type="radio"
                name={`${id}-${side}`}
                value={k}
                checked={stage[side] === k}
                onChange={() => onKind(side, k)}
              />
              <span>
                <i className="pf-relay__ico" aria-hidden="true">
                  {TRIGGER_ICON[k]}
                </i>{" "}
                {t(`relay.${side}Kind.${k}`)}
              </span>
            </label>
          ))}
        </SegGroup>
      ))}
      {choices.length > 1 ? (
        <SegGroup label={t("relay.timeout")} variant="green" testId="relay-timeout">
          {choices.map((sec) => (
            <label key={sec} className="pf-seg__opt">
              <input
                type="radio"
                name={`${id}-timeout`}
                value={sec}
                checked={timeout === sec}
                onChange={() => onTimeout(sec === def.stageTimeoutS ? null : sec)}
              />
              <span>{t("relay.seconds", { n: sec })}</span>
            </label>
          ))}
        </SegGroup>
      ) : (
        <p className="pf-field-row" data-testid="relay-timeout-fixed">
          <span className="pf-field-label">{t("relay.timeout")}</span>
          <b>{t("relay.seconds", { n: def.stageTimeoutS })}</b>
        </p>
      )}
      {mismatch && prev && (
        <div className="pf-banner pf-banner--warn" role="note" data-testid="relay-edit-mismatch">
          <div className="pf-banner__text">
            {t("relay.mismatchDetail", {
              prev: prev.index + 1,
              name: prevName,
              out: t(`relay.kind.${prev.out}`),
              in: t(`relay.kind.${stage.in}`),
            })}
          </div>
          <div className="pf-banner__actions">
            <Button
              variant="ghost"
              className="pf-btn--sm"
              data-testid="relay-align"
              onClick={onAlign}
            >
              {t("relay.align", { kind: t(`relay.kind.${prev.out}`) })}
            </Button>
          </div>
        </div>
      )}
    </>
  );
}

/** 👁 배치도 미리보기 — 위에서 본 구간 배치(6열 뱀 순서 · 되돌아오는 줄은 반대 방향 — DEC-003 §3.3). 모의 실행 아님 */
function PreviewModal({
  stages,
  bad,
  onClose,
}: {
  stages: (RelayDraft["stages"][number] & { name: string })[];
  bad: ReadonlySet<number>;
  onClose: () => void;
}) {
  const t = useT();
  const close = useModalFocus<HTMLButtonElement>();
  const COLS = 6;
  const rows: { i: number; s: (typeof stages)[number] }[][] = [];
  stages.forEach((s, i) => {
    const r = Math.floor(i / COLS);
    (rows[r] ??= []).push({ i, s });
  });
  return (
    <div
      className="pf-modal"
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
    >
      <div
        className="pf-pane pf-pane--hero pf-modal__pane pf-relay__preview"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rl-preview-title"
        data-testid="relay-preview-modal"
      >
        <h2 id="rl-preview-title" className="pf-h3">
          {t("relay.previewTitle")}
        </h2>
        <p className="pf-muted">{t("relay.previewNote")}</p>
        <div className="pf-relay__map">
          {rows.map((row, r) => (
            <div
              key={r}
              className={`pf-relay__maprow${r % 2 ? " pf-relay__maprow--back" : ""}`}
              data-row={r}
            >
              {row.map(({ i, s }) => (
                <div
                  key={s.pid}
                  className={`pf-relay__cell${bad.has(i) ? " pf-relay__cell--bad" : ""}`}
                >
                  <b>{i + 1}</b>
                  <span>{s.name}</span>
                  <span aria-hidden="true">
                    {TRIGGER_ICON[s.in]}→{TRIGGER_ICON[s.out]}
                  </span>
                </div>
              ))}
            </div>
          ))}
        </div>
        <div className="pf-row">
          <Button ref={close} variant="ghost" onClick={onClose} data-testid="relay-preview-close">
            {t("relay.close")}
          </Button>
        </div>
      </div>
    </div>
  );
}

const STATUS_CHIP: Record<RelayStageRun["status"], string> = {
  waiting: "",
  armed: "pf-chip-static--info",
  running: "pf-chip-static--run",
  done: "pf-chip-static--ok",
  failed: "pf-chip-static--warn",
  skipped: "pf-chip-static--dash",
};

/** 시작 뒤 — 잠긴 배치 + 진행 표(교사) · ⏸/▶ · 지금 구간 ⟲ 다시 · ⏭ 건너뛰기 */
function RelayProgress({
  info,
  busy,
  problem,
  roomPaused,
  roomId,
  onCmd,
  onNewRound,
}: {
  info: RelayInfo;
  busy: string | null;
  problem: string | null;
  roomPaused: boolean;
  roomId: string;
  onCmd: (op: RelayOp, stage?: number) => void;
  /** 완성 뒤 새 판(같은 순서·설정으로 Setup) */
  onNewRound?: () => void;
}) {
  const t = useT();
  const run = new Map(info.stages.map((s) => [s.stage, s]));
  const done = info.stages.filter((s) => s.status === "done").length;
  const skipped = info.stages.filter((s) => s.status === "skipped").length;
  const conn = new Map(info.students.map((s) => [s.pid, s.connection]));
  const all = info.state === "allDone";
  return (
    <div className="pf-relay" data-testid="fs-08-01-panel" data-state={info.state}>
      <section className="pf-pane pf-pane--hero" aria-labelledby="rl-run">
        <div className="pf-relay__head">
          <h2 id="rl-run" className="pf-h3">
            {t("relay.runTitle")}
          </h2>
          <span className="pf-chip-static pf-chip-static--dash" data-testid="relay-locked-chip">
            {t("relay.lockedChip")}
          </span>
          {!all && (
            <div className="pf-row pf-rc__end">
              {info.paused ? (
                <Button
                  variant="green"
                  className="pf-btn--sm"
                  disabled={busy !== null}
                  data-testid="relay-resume"
                  onClick={() => onCmd("resume")}
                >
                  {t("relay.resume")}
                </Button>
              ) : (
                <Button
                  variant="accent"
                  className="pf-btn--sm"
                  disabled={busy !== null}
                  data-testid="relay-pause"
                  onClick={() => onCmd("pause")}
                >
                  {t("relay.pause")}
                </Button>
              )}
            </div>
          )}
        </div>
        {all && (
          <div className="pf-banner pf-banner--ok" role="status" data-testid="relay-all-done">
            <div className="pf-banner__text">
              {t("relay.allDone", { done, skipped, n: info.plan.length })}
            </div>
            <div className="pf-banner__actions pf-row">
              {onNewRound && (
                <Button
                  variant="accent"
                  className="pf-btn--sm"
                  disabled={busy !== null}
                  title={t("relay.newRoundHint")}
                  data-testid="relay-new-round"
                  onClick={onNewRound}
                >
                  {t("relay.newRound")}
                </Button>
              )}
              {info.render && (
                <span
                  className={`pf-chip-static ${info.render.status === "done" ? "pf-chip-static--ok" : "pf-chip-static--dash"}`}
                  data-testid="relay-render"
                  data-status={info.render.status}
                >
                  {t(`relay.render.${info.render.status}`, { n: info.render.attempts })}
                </span>
              )}
              {info.render && (
                <ButtonLink
                  to={videoPath(roomId)}
                  variant="ghost"
                  className="pf-btn--sm"
                  data-testid="relay-video"
                >
                  {t("relay.render.open")}
                </ButtonLink>
              )}
              {!ROOM_REPLAY_PLAYER ? null : info.replayId ? (
                <ButtonLink
                  to={`/replay/${encodeURIComponent(info.replayId)}`}
                  variant="ghost"
                  className="pf-btn--sm"
                  data-testid="relay-replay"
                >
                  {t("relay.replay")}
                </ButtonLink>
              ) : (
                <span className="pf-muted" data-testid="relay-replay-wait">
                  {t("relay.replayWait")}
                </span>
              )}
            </div>
          </div>
        )}
        {problem && (
          <p className="pf-field-err" role="alert" data-testid="relay-problem">
            {problem}
          </p>
        )}
        {info.paused && !all && (
          <p className="pf-muted" role="status" data-testid="relay-paused">
            {t(roomPaused ? "relay.pausedRoom" : "relay.paused")}
          </p>
        )}
        <ol className="pf-relay__bar" aria-label={t("relay.barLabel")} data-testid="relay-bar">
          {info.plan.map((p, i) => {
            const st = run.get(p.stage)?.status ?? "waiting";
            return (
              <li
                key={p.pid}
                className={`pf-relay__tick pf-relay__tick--${st}${info.current === p.stage ? " pf-relay__tick--cur" : ""}`}
                data-status={st}
                title={`${i + 1} · ${t(`relay.status.${st}`)}`}
                aria-current={info.current === p.stage ? "step" : undefined}
              >
                {i + 1}
                {/* P3-S6 접근성 — title 은 화면 읽기에 믿을 수 없다 → 상태를 글로 */}
                <span className="pf-sr-only"> · {t(`relay.status.${st}`)}</span>
              </li>
            );
          })}
        </ol>
      </section>
      <section className="pf-pane" aria-labelledby="rl-table">
        <h2 id="rl-table" className="pf-h3">
          {t("relay.tableTitle")}
        </h2>
        <div className="pf-tbl-scroll" tabIndex={0} aria-labelledby="rl-table">
          <table className="pf-tbl pf-relay__tbl" data-testid="relay-table">
            <thead>
              <tr>
                <th scope="col">{t("relay.colStage")}</th>
                <th scope="col">{t("relay.colName")}</th>
                <th scope="col">{t("relay.colKinds")}</th>
                <th scope="col">{t("relay.colStatus")}</th>
                <th scope="col">{t("relay.colAttempt")}</th>
                <th scope="col">{t("relay.colAct")}</th>
              </tr>
            </thead>
            <tbody>
              {info.plan.map((p) => {
                const r = run.get(p.stage);
                const st = r?.status ?? "waiting";
                const cur = info.current === p.stage && !all;
                const c = conn.get(p.pid) ?? "offline";
                const canRetry = cur && (st === "failed" || st === "running");
                const canSkip = cur && (st === "armed" || st === "running" || st === "failed");
                return (
                  <tr
                    key={p.pid}
                    data-stage={p.stage + 1}
                    data-status={st}
                    className={cur ? "pf-relay__row--cur" : undefined}
                  >
                    <th scope="row">{t("relay.stage", { n: p.stage + 1 })}</th>
                    <td>
                      {p.nickname}
                      {c !== "online" && (
                        <span className="pf-chip-static pf-chip-static--err pf-relay__conn">
                          {t(`relay.conn.${c}`)}
                        </span>
                      )}
                    </td>
                    <td>
                      <KindTag k={p.in} /> → <KindTag k={p.out} />
                    </td>
                    <td>
                      <span
                        className={`pf-chip-static ${STATUS_CHIP[st]}`}
                        data-testid="relay-status"
                      >
                        {t(`relay.status.${st}`)}
                      </span>
                      {st === "failed" && r?.lastFail && (
                        <span className="pf-muted pf-relay__left" data-testid="relay-fail-reason">
                          {" "}
                          ({t(`relay.failReason.${r.lastFail}`)})
                        </span>
                      )}
                      {cur && r?.remainingS != null && (
                        <span className="pf-muted pf-relay__left">
                          {" "}
                          {t(st === "failed" ? "relay.autoRetryIn" : "relay.leftS", {
                            n: r.remainingS,
                          })}
                        </span>
                      )}
                    </td>
                    <td data-testid="relay-attempt">{r && r.attempt > 0 ? r.attempt : "—"}</td>
                    <td>
                      {cur && (
                        <div className="pf-row">
                          <Button
                            variant="ghost"
                            className="pf-btn--sm"
                            disabled={!canRetry || busy !== null}
                            aria-label={t("relay.retryOf", { n: p.stage + 1 })}
                            data-testid="relay-retry"
                            onClick={() => onCmd("retry", p.stage)}
                          >
                            {t("relay.retry")}
                          </Button>
                          <Button
                            variant="ghost"
                            className="pf-btn--sm"
                            disabled={!canSkip || busy !== null}
                            aria-label={t("relay.skipOf", { n: p.stage + 1 })}
                            data-testid="relay-skip"
                            onClick={() => onCmd("skip", p.stage)}
                          >
                            {t("relay.skip")}
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <p className="pf-muted">{t("relay.runNote")}</p>
      </section>
    </div>
  );
}
